// =====================================================================
// SecuritePlan.jsx — M-010 (25-08-2026)
// Le plan du site comme INTERFACE.
// =====================================================================
// Trois usages du même plan, demandés par Silviu :
//
//  1. la tournée : on voit d'un coup d'œil ce qui reste à vérifier
//  2. signaler un problème N'IMPORTE QUEL JOUR — « un rack plié » — sans
//     attendre le vendredi
//  3. corriger la carte : glisser une pastille, ou cliquer pour ajouter un
//     objet qui n'est pas dessiné sur le plan d'évacuation (les 3 trousses,
//     les 23 engins)
//
// Rappel de la distinction que porte le modèle : un rack plié n'a pas de
// victime. Il crée une ACTION CORRECTIVE, pas une inscription au registre
// des premiers soins — lequel contient des données de santé.
//
// Les positions sont normalisées 0..1 : le plan peut être re-rendu à une
// autre résolution sans déplacer un seul point.
// =====================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { safetyPlans, safetyActions, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  todo: '#94a3b8', due: '#d97706', done: '#16a34a', nok: '#dc2626',
  okBg: '#effaf3', warnBg: '#fdf5e7', critBg: '#fdf0f0', bg: '#f8fafc',
};

const STATE_LABEL = {
  todo: 'à vérifier', due: 'échéance atteinte', done: 'vérifié', nok: 'non-conformité',
};
const KIND_LABEL = {
  EXTINCTEUR: 'Extincteur', DEVIDOIR: 'Dévidoir (RIA)',
  ALARME: 'Déclencheur d’alarme', SORTIE_SECOURS: 'Sortie de secours',
  AVERTISSEMENT: 'Avertissement', BOITE_SECOURS: 'Trousse de secours',
  MACHINE: 'Engin / machine', RACK: 'Rack', ECHELLE: 'Échelle',
  QUAI: 'Quai', AUTRE: 'Autre',
  ZONE_RECEPTION: 'Zone réception', ZONE_PACKING: 'Zone packing',
  PARKING_RETRACT: 'Parking retract', PARKING_TRANSPAL: 'Parking transpalettes',
  ZONE: 'Zone',
};
// Types qui sont des SURFACES : on les trace, on ne les pointe pas.
const ZONE_KINDS = ['ZONE_RECEPTION', 'ZONE_PACKING', 'PARKING_RETRACT',
  'PARKING_TRANSPAL', 'RACK', 'ZONE'];
// Taille du pictogramme sur le plan. Les panneaux d'incendie sont un peu
// plus gros : ce sont ceux qu'on cherche en urgence. Plafonnee a 16 px :
// un extincteur et un devidoir sont accroches cote a cote, donc a une
// dizaine de pixels l'un de l'autre a l'ecran, et au-dela ils se
// recouvrent (verifie sur previsualisation).
const KIND_SIZE = {
  EXTINCTEUR: 16, DEVIDOIR: 16, ALARME: 15, SORTIE_SECOURS: 15,
  AVERTISSEMENT: 14, BOITE_SECOURS: 18, MACHINE: 14,
};

// Les pictogrammes sont les images REELLES du plan d'evacuation (les
// trousses et les engins, absents du plan, sont dessines). Celui qui
// regarde l'ecran voit donc le meme panneau que celui qui est au mur.
const PICTO = (a) => (a.picto ? `/picto/${a.picto}` : null);

// Ordre de la legende : d'abord ce qu'on cherche en urgence.
const LEGENDE = [
  ['EXTINCTEUR', 'f001-extincteur.png'],
  ['DEVIDOIR', 'f002-devidoir.png'],
  ['ALARME', 'f005-alarme.png'],
  ['SORTIE_SECOURS', 'e001-sortie-secours.png'],
  ['BOITE_SECOURS', 'e003-trousse.svg'],
  ['AVERTISSEMENT', 'w012-danger-electrique.png'],
  ['MACHINE', 'engin.svg'],
];

// Un objet qui a un second coin est une ZONE (un rack), pas un point.
const isZone = (a) => a.planX2 != null && a.planY2 != null;

const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1rem' };
const inp = { width: '100%', padding: '0.55rem', border: `1px solid ${C.line}`, borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: C.mute, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.04em' };
const btn = (bg, fg = '#fff') => ({
  background: bg, color: fg, border: bg === '#fff' ? `1px solid ${C.line}` : '1px solid transparent',
  borderRadius: 7, padding: '0.55rem 0.9rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
});

export default function SecuritePlan({ initialScan = null }) {
  const { can } = useAuth();
  const canEdit = can('maintenance.plan.all');

  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [unplaced, setUnplaced] = useState([]);
  const [actions, setActions] = useState([]);
  const [sel, setSel] = useState(null);          // pastille ouverte
  const [edit, setEdit] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);

  // signalement
  const [rep, setRep] = useState({ title: '', description: '', severity: 'NORMALE' });
  const [vocab, setVocab] = useState({});
  const [showAll, setShowAll] = useState(false);   // sortir du raffinement par type
  // ajout d'un objet
  const [add, setAdd] = useState(null);          // { x, y, kind, name }
  // pose d'un objet existant (réserve)
  const [placing, setPlacing] = useState(null);  // asset non positionné
  const [drawing, setDrawing] = useState(null);  // { x0, y0, x1, y1 } pendant le tracé
  const drawRef = useRef(null);

  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const scanFait = useRef(false);

  const load = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const [a, u, act] = await Promise.all([
        safetyPlans.assets(pid), safetyPlans.unplaced(), safetyActions.list(),
      ]);
      setAssets(a); setUnplaced(u); setActions(act);

      // Arrivee par un autocollant : on ouvre directement l'objet scanne, et
      // le formulaire de signalement avec. Une seule fois — si la personne
      // referme la fiche, on ne la lui rouvre pas de force.
      if (initialScan && !scanFait.current) {
        scanFait.current = true;
        const cible = [...a, ...u].find(
          (x) => String(x.code).toUpperCase() === String(initialScan).toUpperCase());
        if (cible) {
          setSel(cible);
          setShowAll(false);
          setRep({ title: '', description: '', severity: 'NORMALE', where: '' });
        } else {
          setErr(`Aucun objet ne porte le code « ${initialScan} ». `
            + 'Vérifiez l\'autocollant, ou signalez depuis le plan.');
        }
      }
    } catch (e) { setErr(errMsg(e)); }
  }, [initialScan]);

  // Suggestions servies par l'API : on peut enrichir les listes sans
  // reconstruire le frontend.
  useEffect(() => {
    (async () => {
      try { setVocab(await safetyActions.vocab()); } catch (_) { /* perm */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = await safetyPlans.list();
        setPlans(p);
        if (p.length) { setPlanId(p[0].id); load(p[0].id); }
      } catch (e) { setErr(errMsg(e)); }
    })();
  }, [load]);

  const tally = assets.reduce((acc, a) => { acc[a.state] = (acc[a.state] || 0) + 1; return acc; }, {});

  // --- coordonnées relatives depuis un événement pointeur ---------------
  function relPos(e) {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  }

  // --- glisser une pastille --------------------------------------------
  function onPinDown(e, a) {
    if (!edit) return;
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { id: a.id, code: a.code };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onMove(e) {
    if (drawRef.current) {
      const { x, y } = relPos(e);
      setDrawing({ ...drawRef.current, x1: x, y1: y });
      return;
    }
    if (!dragRef.current) return;
    const { x, y } = relPos(e);
    setAssets((list) => list.map((a) => (a.id === dragRef.current.id ? { ...a, planX: x, planY: y } : a)));
  }
  async function onUp(e) {
    if (drawRef.current) {
      const d0 = drawRef.current;
      drawRef.current = null;
      const { x, y } = relPos(e);
      const w = Math.abs(x - d0.x0); const h = Math.abs(y - d0.y0);
      setDrawing(null);
      // Un tracé minuscule = un clic : on retombe sur « ajouter un point ».
      if (w < 0.012 || h < 0.008) { setAdd({ x, y, kind: 'BOITE_SECOURS', name: '' }); return; }
      setAdd({
        x: Math.min(d0.x0, x), y: Math.min(d0.y0, y),
        x2: Math.max(d0.x0, x), y2: Math.max(d0.y0, y),
        kind: 'ZONE_RECEPTION', name: '',
      });
      return;
    }
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    const a = assets.find((x) => x.id === d.id);
    if (!a) return;
    try {
      await safetyPlans.setPosition(a.id, { planId, x: a.planX, y: a.planY });
      setFlash(`${a.code} repositionné — l'import ne l'écrasera plus.`);
    } catch (e2) { setErr(errMsg(e2)); load(planId); }
  }

  // --- tracer une zone : on appuie et on tire, comme une sélection -------
  function onPlanDown(e) {
    if (!edit || placing || add || dragRef.current) return;
    if (e.target !== e.currentTarget && e.target.tagName !== 'IMG') return;  // pas sur une pastille
    const { x, y } = relPos(e);
    drawRef.current = { x0: x, y0: y };
    setDrawing({ x0: x, y0: y, x1: x, y1: y });
  }

  // --- clic sur le plan : ajouter, ou poser un objet de la réserve ------
  async function onPlanClick(e) {
    if (!edit || dragRef.current) return;
    const { x, y } = relPos(e);
    if (placing) {
      try {
        await safetyPlans.setPosition(placing.id, { planId, x, y });
        setFlash(`${placing.code} posé sur le plan.`);
        setPlacing(null);
        load(planId);
      } catch (e2) { setErr(errMsg(e2)); }
      return;
    }
    // l'ajout est décidé dans onUp (clic court = point, tracé = zone)
  }

  async function doAdd(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const a = await safetyPlans.createAsset({
        planId, x: add.x, y: add.y, x2: add.x2 ?? null, y2: add.y2 ?? null,
        kind: add.kind, name: add.name,
      });
      setFlash(`${a.code} créé — c'est ce code qu'on imprime en QR.`);
      setAdd(null);
      load(planId);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  }

  async function doReport(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const a = await safetyActions.report({
        assetId: sel.id,
        // L'emplacement scanne est un CHAMP, pas un bout de phrase : on
        // pourra chercher, regrouper et compter par emplacement.
        locationCode: (rep.where || '').trim() || null,
        title: rep.title,
        description: rep.description || null,
        severity: rep.severity,
      });
      setFlash(`${a.ref} ouverte — échéance ${String(a.dueDate).slice(0, 10)}.`);
      setRep({ title: '', description: '', severity: 'NORMALE', where: '' });
      load(planId);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  }

  const selActions = sel ? actions.filter((a) => a.assetId === sel.id) : [];

  // Un extincteur et un rack n'ont pas les memes defauts : on restreint la
  // liste au type d'objet scanne, sans jamais empecher d'ecrire autre chose.
  const refine = (sel && (vocab.byKind || {})[sel.kind]) || null;
  const opts = (field) => {
    const full = vocab[field] || [];
    if (showAll || !refine || !refine[field]) return full;
    const keep = new Set(refine[field]);
    return full.filter((v) => keep.has(v));
  };
  const isRefined = (field) => Boolean(!showAll && refine && refine[field]);

  return (
    <div>
      {err && <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf', color: '#991B1B' }}>{err}</div>}
      {flash && <div style={{ ...card, background: C.okBg, borderColor: '#cfead9', color: '#065F46', fontWeight: 600 }}>{flash}</div>}

      {/* ------------------ barre d'outils ------------------ */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        {plans.length > 1 && (
          <select style={{ ...inp, width: 'auto' }} value={planId || ''}
            onChange={(e) => { setPlanId(e.target.value); setSel(null); load(e.target.value); }}>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 13, fontSize: '0.85rem', color: C.ink, flexWrap: 'wrap' }}>
          {['nok', 'due', 'done', 'todo'].map((s) => (
            <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <i style={{ width: 11, height: 11, borderRadius: '50%', background: C[s], boxShadow: '0 0 0 1.5px #fff, 0 1px 2px rgba(0,0,0,.3)' }} />
              {tally[s] || 0} {STATE_LABEL[s]}
            </span>
          ))}
        </div>
        <span style={{ fontSize: '0.8rem', color: C.mute }}>
          · les rectangles sont les racks, les pastilles les objets
        </span>
        {canEdit && (
          <button onClick={() => { setEdit((v) => !v); setPlacing(null); setAdd(null); }}
            style={{ ...btn(edit ? C.brand : '#fff', edit ? '#fff' : C.ink), marginLeft: 'auto' }}>
            {edit ? 'Terminer la correction' : 'Corriger la carte'}
          </button>
        )}
      </div>

      {edit && (
        <div style={{ ...card, background: '#f5f2f7', borderColor: C.line, fontSize: '0.87rem' }}>
          <b>Mode correction.</b> Glissez une pastille pour la déplacer.
          Cliquez sur une zone vide pour ajouter un objet ponctuel.
          <b> Tirez un rectangle</b> pour délimiter une zone — réception, packing,
          parking retract, parking transpalettes.
          {placing
            ? <span style={{ color: C.brand, fontWeight: 700 }}> — cliquez où se trouve {placing.code}.</span>
            : ' Les positions viennent du plan, pas du terrain : à confirmer une fois sur place avant de coller les QR.'}
        </div>
      )}

      {/* ------------------ le plan ------------------ */}
      {planId && (
        <div ref={wrapRef}
          onPointerDown={onPlanDown}
          onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
          onClick={onPlanClick}
          style={{
            position: 'relative', border: `1px solid ${C.line}`, borderRadius: 10,
            overflow: 'hidden', background: '#fff', marginBottom: '1rem',
            cursor: edit ? (placing ? 'crosshair' : 'copy') : 'default',
            touchAction: edit ? 'none' : 'auto',
          }}>
          <img src={safetyPlans.imageUrl(planId)} alt="Plan du site"
            style={{ display: 'block', width: '100%', height: 'auto' }} draggable={false} />
          {/* Tracé en cours */}
          {drawing && (
            <div style={{
              position: 'absolute',
              left: `${Math.min(drawing.x0, drawing.x1) * 100}%`,
              top: `${Math.min(drawing.y0, drawing.y1) * 100}%`,
              width: `${Math.abs(drawing.x1 - drawing.x0) * 100}%`,
              height: `${Math.abs(drawing.y1 - drawing.y0) * 100}%`,
              border: `2px dashed ${C.brand}`, background: `${C.brand}1f`,
              borderRadius: 4, pointerEvents: 'none', zIndex: 6,
            }} />
          )}

          {/* Zones (racks) en dessous : cliquables sur toute leur longueur */}
          {assets.filter((a) => a.planX != null && isZone(a)).map((a) => {
            const isSel = sel?.id === a.id;
            const x = Math.min(a.planX, a.planX2) * 100;
            const y = Math.min(a.planY, a.planY2) * 100;
            const w = Math.abs(a.planX2 - a.planX) * 100;
            const h = Math.abs(a.planY2 - a.planY) * 100;
            return (
              <button key={a.id} type="button"
                onClick={(e) => { e.stopPropagation(); setSel(a); setShowAll(false); setRep({ title: '', description: '', severity: 'NORMALE', where: '' }); }}
                title={`${a.code} · ${a.name} · ${STATE_LABEL[a.state]}`}
                style={{
                  position: 'absolute', left: `${x}%`, top: `${y}%`,
                  width: `${w}%`, height: `${h}%`, minHeight: 6,
                  border: `1.5px solid ${C[a.state] || C.todo}`,
                  background: `${C[a.state] || C.todo}${isSel ? '55' : '26'}`,
                  borderRadius: 3, padding: 0, cursor: 'pointer',
                  boxShadow: isSel ? `0 0 0 2px ${C.brand}` : 'none',
                  zIndex: isSel ? 3 : 0,
                }} />
            );
          })}

          {/* Points (extincteurs, sorties, trousses, engins) au-dessus */}
          {assets.filter((a) => a.planX != null && !isZone(a)).map((a) => {
            const size = KIND_SIZE[a.kind] || 18;
            const isSel = sel?.id === a.id;
            const src = PICTO(a);
            const d = isSel ? size + 8 : size;
            return (
              <button key={a.id} type="button"
                onPointerDown={(e) => onPinDown(e, a)}
                onClick={(e) => { e.stopPropagation(); setSel(a); setShowAll(false); setRep({ title: '', description: '', severity: 'NORMALE', where: '' }); }}
                title={`${a.code} · ${KIND_LABEL[a.kind] || a.kind} · ${STATE_LABEL[a.state]}`}
                style={{
                  position: 'absolute', left: `${a.planX * 100}%`, top: `${a.planY * 100}%`,
                  transform: 'translate(-50%,-50%)', width: d, height: d,
                  // Un panneau est carre : on ne l'enferme pas dans un rond.
                  borderRadius: src ? 3 : '50%', border: 0, padding: 0,
                  background: src ? '#fff' : (C[a.state] || C.todo),
                  // L'etat passe dans l'ANNEAU, puisque le centre est occupe
                  // par le panneau. Le lisere blanc est indispensable : un
                  // anneau rouge sur un panneau rouge ne se verrait pas.
                  boxShadow: isSel
                    ? `0 0 0 2px #fff, 0 0 0 4px ${C.brand}`
                    : `0 0 0 1.5px #fff, 0 0 0 3.5px ${C[a.state] || C.todo}, 0 1px 3px rgba(0,0,0,.35)`,
                  cursor: edit ? 'grab' : 'pointer', zIndex: isSel ? 4 : 1,
                  overflow: 'hidden', lineHeight: 0,
                }}>
                {src && (
                  <img src={src} alt="" draggable={false} style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    display: 'block', pointerEvents: 'none',
                  }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Legende des symboles : ce sont les panneaux reels du plan
          d'evacuation, donc les memes qu'au mur. La couleur de l'anneau
          dit l'etat, le panneau dit de quoi il s'agit. */}
      {!!assets.length && (
        <div style={{ ...card, display: 'flex', gap: '1.1rem', flexWrap: 'wrap',
          alignItems: 'center', padding: '.7rem 1rem' }}>
          <span style={{ fontSize: '.78rem', fontWeight: 600, color: C.mute,
            textTransform: 'uppercase', letterSpacing: '.04em' }}>Symboles</span>
          {LEGENDE.map(([kind, picto]) => {
            const n = assets.filter((a) => a.kind === kind).length;
            if (!n) return null;
            return (
              <span key={kind} style={{ display: 'inline-flex', gap: 6,
                alignItems: 'center', fontSize: '.84rem', color: C.ink }}>
                <img src={`/picto/${picto}`} alt="" style={{ width: 20, height: 20,
                  objectFit: 'contain' }} />
                {KIND_LABEL[kind] || kind} <b>{n}</b>
              </span>
            );
          })}
        </div>
      )}

      {/* ------------------ ajouter un objet ------------------ */}
      {add && (
        <form onSubmit={doAdd} style={{ ...card, borderLeft: `4px solid ${C.brand}` }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>
            {add.x2 != null ? 'Nommer cette zone' : 'Ajouter un objet ici'}
          </div>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))' }}>
            <div>
              <label style={lbl}>Type</label>
              <select style={inp} value={add.kind} onChange={(e) => setAdd((a) => ({ ...a, kind: e.target.value }))}>
                {Object.entries(KIND_LABEL)
                  .filter(([k]) => (add.x2 != null ? ZONE_KINDS.includes(k) : !ZONE_KINDS.includes(k)))
                  .map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Nom</label>
              <input style={inp} required value={add.name} autoFocus
                onChange={(e) => setAdd((a) => ({ ...a, name: e.target.value }))}
                placeholder="Trousse zone packing…" />
            </div>
          </div>
          <p style={{ fontSize: '0.82rem', color: C.mute, margin: '9px 0 0' }}>
            Le code sera généré automatiquement — c'est lui qu'on imprime en QR.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
            <button type="submit" disabled={busy} style={btn(C.brand)}>Créer</button>
            <button type="button" onClick={() => setAdd(null)} style={btn('#fff', C.ink)}>Annuler</button>
          </div>
        </form>
      )}

      {/* ------------------ réserve : objets sans position ------------------ */}
      {edit && unplaced.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Objets sans position ({unplaced.length})
          </div>
          <p style={{ fontSize: '0.84rem', color: C.mute, margin: '0 0 10px' }}>
            Ils ne sont pas dessinés sur le plan d'évacuation. Choisissez-en un,
            puis cliquez sur le plan à l'endroit exact.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {unplaced.map((u) => (
              <button key={u.id} onClick={() => setPlacing(u)}
                style={{
                  ...btn(placing?.id === u.id ? C.brand : '#fff', placing?.id === u.id ? '#fff' : C.ink),
                  fontSize: '0.8rem', padding: '4px 9px',
                }}>
                {u.code} · {u.name.length > 26 ? u.name.slice(0, 26) + '…' : u.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ------------------ pastille sélectionnée ------------------ */}
      {sel && (
        <div style={{ ...card, borderLeft: `4px solid ${C[sel.state]}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.02rem' }}>{sel.code} — {sel.name}</div>
              <div style={{ color: C.mute, fontSize: '0.85rem' }}>
                {KIND_LABEL[sel.kind] || sel.kind} · <span style={{ color: C[sel.state], fontWeight: 600 }}>{STATE_LABEL[sel.state]}</span>
                {sel.serialNumber ? ` · n° ${sel.serialNumber}` : ''}
                {sel.provider ? ` · ${sel.provider}` : ''}
              </div>
            </div>
            <button onClick={() => setSel(null)} style={{ ...btn('#fff', C.mute), padding: '3px 9px' }}>Fermer</button>
          </div>

          {selActions.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>
                Actions correctives sur cet objet
              </div>
              {selActions.map((a) => (
                <div key={a.id} style={{ fontSize: '0.86rem', padding: '4px 0', color: a.overdue ? C.nok : C.ink }}>
                  <b>{a.ref}</b>
                  {a.locationCode && (
                    <code style={{
                      background: '#f1f5f9', padding: '1px 5px', borderRadius: 4,
                      margin: '0 5px', fontSize: '0.8rem',
                    }}>{a.locationCode}</code>
                  )}
                  {' · '}{a.title} · {a.severity.toLowerCase()} ·{' '}
                  {a.status === 'CLOTUREE' ? 'clôturée' : `échéance ${String(a.dueDate).slice(0, 10)}`}
                  {a.overdue && ' — EN RETARD'}
                  {a.ownerName ? ` · ${a.ownerName}` : ' · sans responsable'}
                </div>
              ))}
            </div>
          )}

          <form onSubmit={doReport} style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>
              Signaler un problème sur cet objet
            </div>
            <div style={{ display: 'grid', gap: 9 }}>
              {refine && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, flexWrap: 'wrap', background: '#f5f2f7',
                  border: `1px solid ${C.line}`, borderRadius: 6,
                  padding: '0.45rem 0.65rem', fontSize: '0.81rem', color: C.ink,
                }}>
                  <span>Listes adaptées à <b>{KIND_LABEL[sel.kind] || sel.kind}</b></span>
                  <button type="button" onClick={() => setShowAll((v) => !v)}
                    style={{
                      border: `1px solid ${C.line}`, background: '#fff', borderRadius: 6,
                      padding: '3px 9px', fontSize: '0.77rem', cursor: 'pointer',
                      color: C.brand, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                    {showAll ? 'Revenir aux suggestions' : 'Tout afficher'}
                  </button>
                </div>
              )}
              <div>
                <label style={lbl}>
                  Quoi{isRefined('title') && <span style={{ color: C.brand }}> · liste réduite</span>}
                </label>
                <input style={inp} required list="dl-act-title" value={rep.title}
                  onChange={(e) => setRep((r) => ({ ...r, title: e.target.value }))}
                  placeholder="Choisir ou écrire…" />
                <datalist id="dl-act-title">
                  {opts('title').map((v) => <option key={v} value={v} />)}
                </datalist>
              </div>
              <div style={{ display: 'grid', gap: 9, gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
                <div>
                  <label style={lbl}>Gravité</label>
                  <select style={inp} value={rep.severity}
                    onChange={(e) => setRep((r) => ({ ...r, severity: e.target.value }))}>
                    <option value="NORMALE">Normale — 30 jours</option>
                    <option value="HAUTE">Haute — 7 jours</option>
                    <option value="CRITIQUE">Critique — demain</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>
                    Ce qu'il faut faire{isRefined('detail') && <span style={{ color: C.brand }}> · liste réduite</span>}
                  </label>
                  <input style={inp} list="dl-act-detail" value={rep.description}
                    onChange={(e) => setRep((r) => ({ ...r, description: e.target.value }))}
                    placeholder="Choisir ou écrire…" />
                  <datalist id="dl-act-detail">
                    {opts('detail').map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
              </div>
            </div>
            {ZONE_KINDS.includes(sel.kind) && (
              <div style={{
                marginTop: 10, background: '#f5f2f7', border: `1px solid ${C.line}`,
                borderRadius: 8, padding: '0.7rem 0.8rem',
              }}>
                <label style={lbl}>Scanner l'étiquette d'emplacement sur la lisse</label>
                <input style={{ ...inp, fontFamily: 'Consolas, monospace', fontSize: '1.05rem' }}
                  value={rep.where || ''} autoFocus
                  onChange={(e) => setRep((r) => ({ ...r, where: e.target.value }))}
                  placeholder="Scannez, ou tapez le code lu sur la lisse" />
                <p style={{ fontSize: '0.78rem', color: C.mute, margin: '5px 0 0' }}>
                  Chaque cellule porte <b>4 emplacements palettes</b> : scannez
                  <b> n'importe lequel des 4</b>, il identifie la cellule. Rien de
                  nouveau à coller — les étiquettes sont déjà sur les lisses, et
                  c'est bien plus précis qu'un point sur le plan.
                  {' '}Sans scan, l'action reste rattachée à {sel.code} en entier.
                </p>
              </div>
            )}
            <p style={{ fontSize: '0.8rem', color: C.mute, margin: '9px 0 0' }}>
              Ceci n'est pas un accident : personne n'est blessé. Si quelqu'un est
              blessé, ce n'est pas ici : module <b>Accidents &amp; premiers soins</b>, le registre légal.
            </p>
            <button type="submit" disabled={busy} style={{ ...btn(C.due), marginTop: 10 }}>
              Ouvrir une action corrective
            </button>
          </form>
        </div>
      )}

      {/* ------------------ actions ouvertes ------------------ */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '0.8rem 1.1rem', fontWeight: 700, borderBottom: `1px solid ${C.line}` }}>
          Actions correctives ouvertes ({actions.filter((a) => a.status !== 'CLOTUREE').length})
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
            <thead>
              <tr style={{ background: C.bg, textAlign: 'left' }}>
                {['Réf.', 'Objet', 'Emplacement', 'Problème', 'Gravité', 'Échéance', 'Responsable', 'Origine'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', color: C.mute, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${C.line}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '1.1rem', textAlign: 'center', color: C.mute }}>
                  Aucune action ouverte.
                </td></tr>
              )}
              {actions.map((a) => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{a.ref}</td>
                  <td style={{ padding: '8px 10px' }}>{a.asset ? a.asset.code : '—'}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'Consolas, monospace', fontSize: '0.82rem' }}>
                    {a.locationCode || '—'}
                  </td>
                  <td style={{ padding: '8px 10px' }}>{a.title}</td>
                  <td style={{ padding: '8px 10px', color: a.severity === 'CRITIQUE' ? C.nok : (a.severity === 'HAUTE' ? C.due : C.ink), fontWeight: 600 }}>
                    {a.severity.toLowerCase()}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: a.overdue ? C.nok : C.ink, fontVariantNumeric: 'tabular-nums' }}>
                    {a.dueDate ? String(a.dueDate).slice(0, 10) : '—'}{a.overdue && ' ⚠'}
                  </td>
                  <td style={{ padding: '8px 10px', color: a.ownerName ? C.ink : C.due }}>
                    {a.ownerName || 'à attribuer'}
                  </td>
                  <td style={{ padding: '8px 10px', color: C.mute }}>
                    {a.originType === 'ROUTINE' ? 'check-list' : 'signalé'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
